<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
    style="z-index: 1100 !important;"
  >
    <v-card v-cloak>
      <v-card-title class="d-flex justify-space-between">
        {{ $t('console.requestConnection') }}

        <v-btn icon @click="dialog = false;">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text scrollable class="scrollable">
        <v-form @submit.prevent="submitForm" ref="form">

          <!-- Select nodes to share -->
          <div class="mb-4">
            {{ $t('console.selectFolderToShare') }}

            <SelectNodeDialog
              v-model="selectedNodeId"
              #default="slotProps"
              :disabled-node-ids="selectedNodeIdsArray"
            >
              <v-btn
                variant="flat" block
                color="info">
                  <span v-if="slotProps && slotProps.selectedNode">
                    {{ slotProps.selectedNode.name }}
                  </span>
                <span v-else>
                    {{ $t('console.selectFolder') }}
                  </span>
              </v-btn>
            </SelectNodeDialog>
          </div>

          <!-- List of the selected nodes -->
          <div class="mb-2">
            <p v-if="selectedNodes.length > 0">
              {{ $t('console.thisFoldersWillBeShared') }}
            </p>
            <p v-else class="mb-4">
              {{ $t('console.noFolderSelected') }}
            </p>
            <v-list v-if="selectedNodes.length > 0" compact>
              <v-list-item
                v-for="node in selectedNodes"
                :key="node.id"
              >
                <v-list-item-title>
                  {{ node.name }}
                </v-list-item-title>

                <template v-slot:append>
                  <v-btn icon="mdi-close" width="28px" height="28px"
                         @click="removeSelectedNode(node.id)"
                         variant="text"></v-btn>
                </template>
              </v-list-item>
            </v-list>
          </div>

          <!-- Expiration date of the connection link -->
          <div>
            <p>
              {{ $t('console.selectExpirationDate') }}
            </p>
            <v-menu
              v-model="datepickerMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              location="bottom end"
              min-width="auto"
            >
              <template v-slot:activator="{ props }">
                <v-text-field
                  v-bind="props"
                  v-model="expiryDate"
                  :placeholder="$t('routes.pod.menuAccesslink.expiryDate')"
                  :aria-label="$t('routes.pod.menuAccesslink.expiryDate')"
                  :rules="expiryDateRules"
                  append-inner-icon="mdi-calendar"
                  variant="solo-filled"
                  hide-details="auto"
                  readonly
                  required
                  block
                  flat
                  data-cy="podMenu-tools-manageGenericLinks-dateMenu"
                />
              </template>
              <v-date-picker
                v-model="expiryDate"
                @update:model-value="datepickerMenu = false"
                :min="getMinDatepicker"
                show-adjacent-months
                rounded="md"
                hide-details="auto"
                data-cy="podMenu-tools-manageGenericLinks-datePicker"
              />
            </v-menu>
          </div>

          <!-- Tenant admin email input -->
          <div class="mt-4">
            <v-text-field
              v-model="tenantAdminEmailInput"
              :label="$t('console.tenantAdminEmailDescription')"
              :placeholder="$t('console.tenantAdminEmailDescription')"
              :rules="tenantAdminEmailInputRules"
              variant="solo-filled"
              hide-details="auto"
              block
              flat
              data-cy="tenantAdminEmailInput"
            />
          </div>

          <!-- Permissions list -->
          <div class="mt-6">
            <v-row>
              <v-col cols=12 sm=6 offset-sm=3>
                <v-list-subheader>
                  <h3>
                    {{ $t('menus.userPermissions.nodesAndPods') }}
                  </h3>
                </v-list-subheader>

                <div data-cy="dialog-setPermissions-listNodesAndPods" v-if="$canListNodeAndPod()">
                  <v-switch
                    style="margin-top: 0"
                    v-model="nodeAndPodList"
                    v-bind:label="$t('menus.userPermissions.listNodesAndPods')"
                    hide-details
                    density="compact"
                    color="primary"
                    block
                  >
                  </v-switch>
                </div>

                <div data-cy="dialog-setPermissions-controlNodesAndPods" v-if="$canControlNodeAndPod()">
                  <v-switch
                    style="margin-top: 0"
                    v-model="nodeAndPodControl"
                    v-bind:label="$t('menus.userPermissions.controlNodesAndPods')"
                    hide-details
                    density="compact"
                    color="primary"
                    block
                  >
                  </v-switch>
                </div>

                <div data-cy="dialog-setPermissions-deleteNodesAndPods" v-if="$canDeleteNodeAndPod()">
                  <v-switch
                    style="margin-top: 0"
                    v-model="nodeAndPodDelete"
                    v-bind:label="$t('menus.userPermissions.deleteNodesAndPods')"
                    hide-details
                    color="primary"
                    density="compact"
                    block
                  >
                  </v-switch>
                </div>

                <div data-cy="dialog-setPermissions-downloadNodesAndPods" v-if="$canDownloadNodeAndPod()">
                  <v-switch
                    style="margin-top: 0"
                    v-model="nodeAndPodDownload"
                    v-bind:label="$t('menus.userPermissions.downloadNodesAndPods')"
                    hide-details
                    density="compact"
                    color="primary"
                    block
                  >
                  </v-switch>
                </div>

                <div data-cy="dialog-setPermissions-showPodAssets" v-if="$canListAsset()">
                  <v-switch
                    style="margin-top: 0"
                    v-model="assetList"
                    v-bind:label="$t('menus.userPermissions.showPodAssets')"
                    hide-details
                    density="compact"
                    color="primary"
                    block
                  >
                  </v-switch>
                </div>

                <div data-cy="dialog-setPermissions-uploadFilesAndAssets" v-if="$canUploadFileAndAsset()">
                  <v-switch
                    style="margin-top: 0"
                    v-model="fileAndAssetUpload"
                    v-bind:label="$t('menus.userPermissions.uploadFilesAndAssets')"
                    hide-details
                    density="compact"
                    color="primary"
                    block
                  >
                  </v-switch>
                </div>

                <div data-cy="dialog-setPermissions-listShareLinks" v-if="$canListShareLinks()">
                  <v-switch
                    style="margin-top: 0"
                    v-model="listShareLinks"
                    v-bind:label="$t('menus.userPermissions.listShareLinks')"
                    hide-details
                    density="compact"
                    color="primary"
                    block
                  >
                  </v-switch>
                </div>

                <div data-cy="dialog-setPermissions-controlShareLinks" v-if="$canControlShareLinks()">
                  <v-switch
                    style="margin-top: 0"
                    v-model="controlShareLinks"
                    v-bind:label="$t('menus.userPermissions.controlShareLinks')"
                    hide-details
                    density="compact"
                    color="primary"
                    block
                  >
                  </v-switch>
                </div>

                <div data-cy="dialog-setPermissions-archiveNodesAndPods" v-if="$canArchiveNodeAndPod()">
                  <v-switch
                    style="margin-top: 0"
                    v-model="nodeAndPodArchive"
                    v-bind:label="$t('menus.userPermissions.archiveNodesAndPods')"
                    hide-details
                    density="compact"
                    color="primary"
                    block
                  >
                  </v-switch>
                </div>

                <div data-cy="dialog-setPermissions-viewAnalyticsNodeAndPods" v-if="$canViewPodAndNodeAnalytics()">
                  <v-switch
                    style="margin-top: 0"
                    v-model="viewAnalytics"
                    :label="$t('menus.userPermissions.viewAnalytics')"
                    hide-details
                    density="compact"
                    color="primary"
                    block
                  />
                </div>

                <div data-cy="dialog-setPermissions-labelPods" v-if="$canLabelPods()">
                  <v-switch
                    style="margin-top: 0"
                    v-model="podsLabel"
                    :label="$t('menus.userPermissions.labelPods')"
                    hide-details
                    density="compact"
                    color="primary"
                    block
                  />
                </div>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols=12 sm=6 offset-sm=3>
                <v-list-subheader>
                  <h3>
                    {{ $t('menus.userPermissions.services') }}
                  </h3>
                </v-list-subheader>

                <div data-cy="dialog-setPermissions-listPodServices" v-if="$canListPodService()">
                  <v-switch
                    style="margin-top: 0"
                    v-model="podServiceList"
                    v-bind:label="$t('menus.userPermissions.listPodServices')"
                    hide-details
                    density="compact"
                    color="primary"
                    block
                  />
                </div>

                <div data-cy="dialog-setPermissions-controlPodServices" v-if="$canControlPodService()">
                  <v-switch
                    style="margin-top: 0"
                    v-model="podServiceControl"
                    v-bind:label="$t('menus.userPermissions.controlPodServices')"
                    hide-details
                    density="compact"
                    color="primary"
                    block
                  />
                </div>

                <div data-cy="dialog-setPermissions-useFreeServices" v-if="$canRunFreeService()">
                  <v-switch
                    style="margin-top: 0"
                    v-model="useFreeServices"
                    v-bind:label="$t('menus.userPermissions.useFreeServices')"
                    hide-details
                    density="compact"
                    color="primary"
                    block
                  />
                </div>

                <div data-cy="dialog-setPermissions-usePaidServices" v-if="$canRunPaidService()">
                  <v-switch
                    style="margin-top: 0"
                    v-model="usePaidServices"
                    v-bind:label="$t('menus.userPermissions.usePaidServices')"
                    hide-details
                    density="compact"
                    color="primary"
                    block
                  />
                </div>
              </v-col>
            </v-row>
          </div>

          <div v-if="errorMessage" class="bg-error my-2" data-cy="-----">
            {{ errorMessage }}
          </div>

          <div v-if="successMessage" class="bg-success pa-1 mt-1" data-cy="------">
            {{ successMessage }}
          </div>

        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-btn
          icon="mdi-help-circle-outline"
          @click="showingHomeTenantCreateDialogHelpDialog = true"
          rounded="circle"
          width="36"
          height="36"
        />
        <v-spacer />
        <v-btn type="button"
               class="bg-primary"
               elevation=0
               @click="submitForm"
               :loading="isSubmitted"
               :disabled="isSubmitted"
        >
          {{ $t('console.sendRequest') }}
        </v-btn>
        <v-btn variant="text" @click="dialog = false">{{ $t('common.close') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <HomeTenantCreateDialogHelpDialog
    v-model="showingHomeTenantCreateDialogHelpDialog"
  />
</template>

<script>
import { formatDate } from '@/components/formatters.js';
import { useAuthStore } from '@/stores/AuthStore.js';
import { useNodeStore } from '@/stores/NodeStore.js';
import { useTenantConnectionsStore } from '@/stores/TenantConnectionsStore.js';
import SelectNodeDialog from '../../../users/components/dialogs/SelectNodeDialog.vue';
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import DialogModel from '@/components/mixin/DialogModel.js';
import Helper from '@/components/helper';
import { set as setTime } from 'date-fns';
import HomeTenantCreateDialogHelpDialog
  from '@/routes/console/tenant-connections/components/dialogs/HomeTenantCreateDialogHelpDialog.vue';

export default {
  mixins: [ DialogActionKeys, DialogModel ],
  name: 'HomeTenantCreateDialog',
  components: {
    HomeTenantCreateDialogHelpDialog,
    SelectNodeDialog,
  },
  data() {
    return {
      isSubmitted: false,

      errorMessage: '',
      successMessage: '',

      selectedNodeId: null,
      selectedNodeIds: new Set(),

      datepickerMenu: false,
      expiryDateValue: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      expiryDateRules: [
        v => !!v || this.$t('routes.pod.menuAccesslink.expiryDateRequired'),
      ],

      tenantAdminEmailInput: '',
      tenantAdminEmailInputRules: Helper.getEmailValidationRules(this, { required: false }),

      // Permissions
      nodeAndPodList: this.$canListNodeAndPod(),
      nodeAndPodControl: this.$canControlNodeAndPod(),
      nodeAndPodDelete: this.$canDeleteNodeAndPod(),
      nodeAndPodDownload: this.$canDownloadNodeAndPod(),
      assetList: this.$canListAsset(),
      fileAndAssetUpload: this.$canUploadFileAndAsset(),
      listShareLinks: this.$canListShareLinks(),
      controlShareLinks: this.$canControlShareLinks(),
      nodeAndPodArchive: this.$canArchiveNodeAndPod(),
      viewAnalytics: this.$canViewPodAndNodeAnalytics(),
      podsLabel: this.$canLabelPods(),

      podServiceList: this.$canListPodService(),
      podServiceControl: this.$canControlPodService(),
      useFreeServices: false,
      usePaidServices: false,
      showingHomeTenantCreateDialogHelpDialog: false,
    };
  },

  watch: {
    dialog(val) {
      if (!val) {
        this.resetForm();
      }
    },
    selectedNodeId(newVal) {
      if (newVal) {
        this.selectedNodeIds.add(newVal);

        this.collapseSelectedNodeChildren(newVal);
        this.discardSelectedNodeParents(newVal);
      }
    },
  },

  methods: {
    resetForm() {
      this.isSubmitted = false;
      this.errorMessage = '';
      this.successMessage = '';
      this.email = '';

      this.selectedNodeId = null;
      this.selectedNodeIds = new Set();

      // Reset permissions
      this.nodeAndPodList = this.$canListNodeAndPod();
      this.nodeAndPodControl = this.$canControlNodeAndPod();
      this.nodeAndPodDelete = this.$canDeleteNodeAndPod();
      this.nodeAndPodDownload = this.$canDownloadNodeAndPod();
      this.assetList = this.$canListAsset();
      this.fileAndAssetUpload = this.$canUploadFileAndAsset();
      this.listShareLinks = this.$canListShareLinks();
      this.controlShareLinks = this.$canControlShareLinks();
      this.nodeAndPodArchive = this.$canArchiveNodeAndPod();
      this.viewAnalytics = this.$canViewPodAndNodeAnalytics();
      this.podsLabel = this.$canLabelPods();

      this.podServiceList = this.$canListPodService();
      this.podServiceControl = this.$canControlPodService();
      this.useFreeServices = false;
      this.usePaidServices = false;
    },

    removeSelectedNode(nodeId) {
      this.selectedNodeIds.delete(nodeId);
    },

    async submitForm() {
      const { valid } = await this.$refs.form.validate();

      if (!valid || this.selectedNodeIds.size === 0) {
        if (this.selectedNodeIds.size === 0) {
          this.errorMessage = this.$t('console.noFolderSelected');
        }

        return;
      }

      // Prepare the data for the API call
      const formData = {
        selectedNodeIds: Array.from(this.selectedNodeIds.values()),
        expiryDate: setTime(this.expiryDateValue, {
          hours: 23,
          minutes: 59,
          seconds: 59,
          milliseconds: 0,
        }),
        tenantAdminEmail: this.tenantAdminEmailInput,
        permissions: [
          this.nodeAndPodList && 'NodeAndPod.list',
          this.nodeAndPodControl && 'NodeAndPod.control',
          this.nodeAndPodDelete && 'NodeAndPod.delete',
          this.nodeAndPodDownload && 'NodeAndPod.download',
          this.assetList && 'Asset.list',
          this.fileAndAssetUpload && 'FileAndAsset.upload',
          this.listShareLinks && 'ShareLinks.list',
          this.controlShareLinks && 'ShareLinks.control',
          this.nodeAndPodArchive && 'NodeAndPod.archive',
          this.viewAnalytics && 'NodeAndPod.viewAnalytics',
          this.podsLabel && 'Pod.label',
          this.podServiceList && 'PodService.list',
          this.podServiceControl && 'PodService.control',
          this.useFreeServices && 'FreeService.run',
          this.usePaidServices && 'PaidService.run',
        ].filter(Boolean),
      };

      try {
        this.isSubmitted = true;

        const tenantConnectionsStore = useTenantConnectionsStore();
        await tenantConnectionsStore.requestTenantConnection(this.rootNodeId, formData);

        this.dialog = false;

        this.$showMessage(this.$t('console.connectionCreated'), { type: 'success' });
        await tenantConnectionsStore.refreshHomeTenantsList(this.rootNodeId);
      } catch (error) {
        console.error(error);

        if (error?.response?.data?.error) {
          this.errorMessage = this.$localizeMessage(error.response.data.error);
        } else {
          this.errorMessage = this.$t('backend.system-error');
        }
      } finally {
        this.isSubmitted = false;
      }
    },

    /**
     * Collapses multiple child nodes that are descendants of the selected one by the long-most
     * root path of the given node.
     *
     * @param {string} selectedNodeId
     */
    collapseSelectedNodeChildren(selectedNodeId) {
      const nodeStore = useNodeStore();
      const selectedNode = nodeStore.getNodeById(selectedNodeId);
      if (!selectedNode) return;
      const selectedNodeRootPath = selectedNode.rootpath;

      for (const node of this.selectedNodes) {
        if (node.id === selectedNode.id) continue;
        if (node.rootpath.startsWith(selectedNodeRootPath)) this.selectedNodeIds.delete(node.id);
      }
    },
    /**
     * Discard any parents of already selected nodes that are their children.
     *
     * @param {string} selectedNodeId
     */
    discardSelectedNodeParents(selectedNodeId) {
      const nodeStore = useNodeStore();
      const selectedNode = nodeStore.getNodeById(selectedNodeId);
      if (!selectedNode || !selectedNode.parentid) return;
      const selectedNodeRootPath = selectedNode.rootpath;

      for (const node of this.selectedNodes) {
        if (node.rootpath.length < selectedNodeRootPath.length && selectedNodeRootPath.startsWith(node.rootpath)) {
          this.selectedNodeIds.delete(node.id);
        }
      }
    }
  },
  computed: {
    rootNodeId: function () {
      const authStore = useAuthStore();
      return authStore.rootNodeId;
    },
    selectedNodes() {
      const nodeStore = useNodeStore();
      return this.selectedNodeIdsArray.map(nodeId => nodeStore.getNodeById(nodeId));
    },
    selectedNodeIdsArray() {
      return Array.from(this.selectedNodeIds.values());
    },
    expiryDate: {
      get: function () {
        return formatDate(this.expiryDateValue);
      },
      set: function (value) {
        this.expiryDateValue = value;
      },
    },
    getMinDatepicker: function () {
      return formatDate(new Date());
    },
  },
};
</script>
